import type { Role } from './roles';

/**
 * Alert email notifications (Phase 07 Stage 05; DECISIONS.md A-AL5, A-NT1). One delivery is one
 * email about one alert to one portal user. Recipients are identified by user id only; the address
 * is read from the user record when the email is actually sent.
 *
 *  - alert_opened:    an alert that has stayed open for at least 10 minutes (shorter blips send
 *                     nothing). Sent to every active Admin and every active ICT (operations) user.
 *  - alert_recovered: an alert resolved as recovered (Healthy again). Sent only to recipients whose
 *                     alert_opened email for that same alert was accepted by the mail server, and
 *                     who are still active Admin/ICT users. An alert closed by nothing but recovery
 *                     (Unknown never resolves an alert) means Unknown never produces an email.
 *  - No backlog: an alert email is planned only for an alert opened within the last 24 hours, so
 *    switching notifications on never mails old incidents. Recovery emails have no age limit:
 *    their only precondition is the accepted alert email, so a notified alert remains eligible
 *    for a recovery email whenever it recovers.
 *
 * Delivery is at-least-once: a mail server can accept a message just before the worker records
 * it, so a rare duplicate is possible; exactly-once external email is not claimed.
 */
export const NOTIFICATION_KINDS = ['alert_opened', 'alert_recovered'] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/**
 * pending: planned, not yet attempted (or waiting for a retry). sending: claimed by a worker for
 * an attempt. sent: accepted by the mail server (not proof of delivery to the inbox). failed: no
 * further attempts (permanent failure, or the attempt limit reached).
 */
export const DELIVERY_STATUSES = ['pending', 'sending', 'sent', 'failed'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** Fixed, non-secret failure codes recorded on a delivery (never the mail server's own text). */
export const DELIVERY_ERROR_CODES = [
  'smtp_unavailable',
  'smtp_timeout',
  'smtp_tls_error',
  'smtp_auth_failed',
  'smtp_rejected',
  'recipient_rejected',
  'recipient_unavailable',
  'unexpected_error',
] as const;
export type DeliveryErrorCode = (typeof DELIVERY_ERROR_CODES)[number];

/** The approved notification policy (A-NT1). Fixed values, not settings. */
export const NOTIFICATIONS = {
  /** An alert must have been open this long before its alert email is planned. */
  alertDelayMinutes: 10,
  /** Only alerts opened within this window are eligible for an alert email: no backlog. */
  backlogHours: 24,
  /** Attempts per delivery for temporary failures. */
  maxAttempts: 3,
} as const;

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;

/** Who receives alert emails: every active Admin and every active ICT (operations) user. */
export const NOTIFICATION_ROLES = ['admin', 'operations'] as const satisfies readonly Role[];

export function isNotificationRecipient(user: {
  role: Role;
  status: 'active' | 'disabled';
}): boolean {
  return user.status === 'active' && (NOTIFICATION_ROLES as readonly Role[]).includes(user.role);
}

/**
 * The window in which an OPEN alert's opening time must fall for its alert email to be planned at
 * `now`: open at least 10 minutes (opened at or before `openedNoLaterThan`) and opened within the
 * last 24 hours (at or after `openedNoEarlierThan`). Both bounds are inclusive.
 */
export function alertEmailWindow(now: Date): {
  openedNoEarlierThan: Date;
  openedNoLaterThan: Date;
} {
  return {
    openedNoEarlierThan: new Date(now.getTime() - NOTIFICATIONS.backlogHours * HOUR_MS),
    openedNoLaterThan: new Date(now.getTime() - NOTIFICATIONS.alertDelayMinutes * MINUTE_MS),
  };
}

/** Whether an alert email is due at `now` (the alert's own state only; recipients separately). */
export function isAlertEmailDue(
  alert: { openedAt: Date; resolvedAt: Date | null },
  now: Date,
): boolean {
  if (alert.resolvedAt !== null) return false;
  const { openedNoEarlierThan, openedNoLaterThan } = alertEmailWindow(now);
  return alert.openedAt >= openedNoEarlierThan && alert.openedAt <= openedNoLaterThan;
}

/**
 * Whether a recovery email is due to one recipient: the alert has recovered, and that recipient's
 * alert email for this alert was accepted by the mail server. There is no age limit on the
 * recovery: an alert that was legitimately notified stays eligible however long it stays open
 * and whenever it recovers.
 */
export function isRecoveryEmailDue(
  alert: { resolvedAt: Date | null; resolution: 'recovered' | null },
  alertEmailAccepted: boolean,
): boolean {
  return alert.resolution === 'recovered' && alert.resolvedAt !== null && alertEmailAccepted;
}

/**
 * "Alert emails are failing" (Phase 07 Stage 05-C, Admin Dashboard and Notifications): judged
 * from the deliveries' own recorded outcomes only — never from whether email is set up. Emails
 * are failing when, among the outcomes of the last 24 hours, the most recent one is a failure: a
 * final failure, or a temporary failure waiting to be tried again. An acceptance by the mail
 * server clears it. `recipient_unavailable` (the recipient was disabled or lost their role before
 * the send) is not a sending problem and never counts.
 */
export const ALERT_EMAIL_FAILING = {
  /** Outcomes older than this no longer decide the state. */
  windowHours: 24,
} as const;

export type DeliveryOutcome = 'accepted' | 'failed' | 'retrying';

export function alertEmailsFailing(latestOutcome: DeliveryOutcome | null): boolean {
  return latestOutcome === 'failed' || latestOutcome === 'retrying';
}

export function alertEmailFailingSince(now: Date): Date {
  return new Date(now.getTime() - ALERT_EMAIL_FAILING.windowHours * HOUR_MS);
}
